import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { IntegrationDesktopClientMap } from '@/integrations/desktop';
import { hasGetScheduleMixin } from '@/integrations/desktop/mixins/GetScheduleMixin';
import { getIntegrationsSchedule } from '@/pages/api/scribeAPIV2';
import { isTauri } from '@/tauri/utils';
import { formatToDateOnly } from '@/utils/dates';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import {
  type GetIntegrationsScheduleParams,
  type GetIntegrationsScheduleType,
} from '@heidi/integrations/ehr/contracts';
import {
  getEhrConnectionErrorMessage,
  isEhrConnectionError,
} from '@heidi/integrations/ehr/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { type EHRProvider, OnboardingSessionStatus } from '@heidi/schema/ml-scribe';
import { scheduleDateAtom } from '@heidi/session/data';
import { skipToken, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getDefaultStore } from 'jotai';

const GET_INTEGRATIONS_SCHEDULE = 'GetIntegrationsSchedule';

export type UseQueryIntegrationsMixinGetScheduleParams<
  TSelected = GetIntegrationsScheduleType,
> = Partial<GetIntegrationsScheduleParams> & {
  filter?: string[];
  enabled?: boolean;
  select?: (data: GetIntegrationsScheduleType) => TSelected;
};

function buildScheduleQueryKey(params: {
  start_date?: string;
  end_date?: string;
  integrationEhrProvider?: EHRProvider;
  filter?: string[];
}) {
  return [
    GET_INTEGRATIONS_SCHEDULE,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      integrationEhrProvider: params.integrationEhrProvider,
      filter: params.filter,
    },
  ] as const;
}

export const invalidateIntegrationsMixinGetScheduleQuery = () => {
  const jotaiStore = getDefaultStore();
  const scheduleDate = jotaiStore.get(scheduleDateAtom);

  if (!scheduleDate || isNaN(scheduleDate.getTime())) {
    return;
  }

  const formattedScheduleDate = formatToDateOnly(scheduleDate);
  if (!formattedScheduleDate) return;

  void reactQueryClient.invalidateQueries({
    queryKey: [
      GET_INTEGRATIONS_SCHEDULE,
      { start_date: formattedScheduleDate, end_date: formattedScheduleDate },
    ],
  });
};

export function useQueryIntegrationsMixinGetSchedule<
  TSelected = GetIntegrationsScheduleType,
>({
  start_date,
  end_date,
  filter,
  enabled = true,
  select,
}: UseQueryIntegrationsMixinGetScheduleParams<TSelected>) {
  const { t } = useTranslations();
  const {
    integrationEhrProvider,
    isIntegrationOnboardingSession,
    hasMultiple,
    canSyncSchedule,
  } = useIntegrationEhrInfo();

  const { data: integrationOnboardingSession } = useQueryIntegrationOnboardingSessions({
    params: {
      status: OnboardingSessionStatus.completed,
    },
    select: (data) =>
      data.sessions.find(
        (session) => (session.provider as EHRProvider) === integrationEhrProvider
      ),
  });

  const queryKey = buildScheduleQueryKey({
    start_date,
    end_date,
    integrationEhrProvider,
    filter,
  });

  return useQuery<GetIntegrationsScheduleType, Error, TSelected>({
    queryKey,
    select,
    enabled: Boolean(
      enabled && isIntegrationOnboardingSession && (canSyncSchedule || hasMultiple)
    ),
    retry: false,
    queryFn:
      start_date && end_date && integrationEhrProvider && integrationOnboardingSession
        ? async () => {
            const integrationDesktopClient =
              IntegrationDesktopClientMap[integrationEhrProvider];
            if (integrationDesktopClient) {
              // Critical: this is a workaround to allow the e2e tests to run in the browser by mocking the tauri internals
              const hasTauriInvoke =
                typeof window !== 'undefined' &&
                Boolean(window.__TAURI_INTERNALS__?.invoke);
              // Check hasTauriInvoke first: if the e2e shim is active, skip isTauri() entirely.
              // isTauri() throws in non-prod when __TAURI_INTERNALS__ is set without NEXT_PUBLIC_IS_TAURI,
              // which the shim triggers.
              if (!hasTauriInvoke && !isTauri()) {
                return [];
              }

              if (hasGetScheduleMixin(integrationDesktopClient)) {
                return await integrationDesktopClient.getSchedule({
                  startDate: start_date,
                  endDate: end_date,
                  integrationOnboardingSession,
                });
              }

              toast({
                title: 'Error',
                description:
                  'This integration: {integrationEhrProvider} does not support getSchedule',
                tone: 'destructive',
              });
            }

            try {
              const { data } = await getIntegrationsSchedule({
                start_date,
                end_date,
                filter,
              });
              return data.schedule;
            } catch (error) {
              if (isEhrConnectionError(error)) {
                toast({
                  tone: 'destructive',
                  title: t('ehr.ehrConnectionError'),
                  description: getEhrConnectionErrorMessage(error),
                });
              }

              if (error instanceof AxiosError && error.response?.status === 400) {
                logger.error('Schedule fetch failed with 400', { error });
              }

              throw error;
            }
          }
        : skipToken,
  });
}
